// TODO: Schema and Documentation
import { OmniElement, html, css, map, styleMap } from 'omni-ui';
import { produce } from 'immer';
import { rfcColors } from './campaign-audience-rfc-legend.js';

export class CampaignRFCSliders extends OmniElement {
  static get properties() {
    return {
      audiences: { type: Array },
      stage: { type: Object },
      rfc: { type: String },
      percentageIndex: { type: Number },
      audienceIndex: { type: Number },
      barLabels: { type: Object },
      info: { type: Number },
      interest: { type: Number },
      need: { type: Number },
      rfcTitle: { type: String },
      rfcDescription: { type: String },
    };
  }

  static get styles() {
    return css`
      omni-tile {
        --drag-able-z: 6;
        --drag-left: 30%;
        --drag-top: 5%;
        width: 48.7rem;
      }
      omni-tile::part(body-slot) {
        margin: 2.14rem 1.71rem 1.71rem 1.71rem;
      }
      .rfc-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 14.1rem 1fr;
        grid-template-areas:
          'sliders bars'
          'content content';
      }
      .sliders {
        grid-area: sliders;
        display: flex;
        flex-flow: column nowrap;
        justify-content: space-between;
        padding-right: 2.57rem;
        border-right: 1px solid var(--color-border);
      }
      .slider {
        display: flex;
        flex-flow: column nowrap;
        gap: 0.57rem;
      }
      .slider-labels {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: flex-end;
      }
      .slider-label {
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        gap: 0.57rem;
        font-weight: 600;
        line-height: 1.28571rem;
        letter-spacing: 0.00271rem;
      }
      omni-icon {
        margin-bottom: 0.57rem;
      }
      omni-range-slider {
        margin: 0 2.57rem;
      }
      .bars {
        grid-area: bars;
        display: flex;
        flex-flow: column nowrap;
        justify-content: space-between;
        gap: 2.14rem;
        padding: 0 2.57rem 0 0.5rem;
        writing-mode: vertical-lr;
        transform: rotate(180deg);
      }
      .bar-label {
        font-weight: 600;
        line-height: 1.42857rem;
        letter-spacing: 0.00307rem;
      }
      .bar-color {
        height: 50%;
        width: 0.86rem;
      }
      .content-container {
        grid-area: content;
        display: flex;
        flex-flow: column nowrap;
        gap: 2.14rem;
        margin-top: 2.14rem;
      }
      .rfc-content {
        display: flex;
        flex-flow: column nowrap;
        justify-content: flex-start;
        gap: 0.57rem;
      }
      .omni div.rfc-content p.content-label {
        margin-bottom: 0;
        margin-left: 0.5rem;
        font-size: 0.78571rem;
        line-height: 1.07143rem;
        letter-spacing: 0.00186rem;
        color: var(--color-almost-black);
      }
      .button-container {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-end;
        gap: 0.71rem;
      }
    `;
  }

  constructor() {
    super();
    this.audience = {};
    this.stage = {};
    this.percentageIndex = 0;
    this.audienceIndex = 0;
    this.info = 0;
    this.interest = 0;
    this.need = 0;
    this.rfc = '';
    this.rfcTitle = '';
    this.rfcDescription = '';

    // TODO: Move these to a schema
    this.sliderLabels = {
      info: [
        {
          title: 'Information',
          info: 'The focus is on communicating specific information about the brand/product',
        },
        {
          title: 'Associations',
          info: 'The focus is on communicating existing/new brand associations',
        },
      ],
      interest: [
        {
          title: 'Disinterested',
          info: 'The audience is unlikely to be interested in the brand/product',
        },
        {
          title: 'Interested',
          info: 'The audience is likely to be interested in the brand/product',
        },
      ],
      need: [
        {
          title: 'Weak',
          info: 'The brand is weakly associated to the important need-states',
        },
        {
          title: 'Strong',
          info: 'The brand is strongly associated to the important need-states',
        },
      ],
    };
    this.rfcPlaceholders = {
      building: {
        title: 'Build new associations',
        description:
          'To build brand memory structures, focus on a strategy that creates emotional reactions to build new memories.',
      },
      reinforcing: {
        title: 'Reinforce existing associations',
        description:
          'Focus on bringing existing associations to life near the point of purchase/action to maximize the effect of marketing communications.',
      },
      impacting: {
        title: 'Force conscious-processing',
        description:
          'To reach an uninterested audience, achieve cut-through via channels with outreach potential, use channels that break consciousness, and create impactful content.',
      },
      activating: {
        title: 'Activate interested audience',
        description:
          'To reach an interested audience cost-effectively, focus on sensitized audiences likely to notice the message, Provide deeper access to more information.',
      },
    };
    this.barLabels = {
      building: {
        title: 'Building',
        color: rfcColors.building,
        height: '50%',
        opacity: '0.5',
      },
      reinforcing: {
        title: 'Reinforcing',
        color: rfcColors.reinforcing,
        height: '50%',
        opacity: '0.5',
      },
      impacting: {
        title: 'Impacting',
        color: rfcColors.impacting,
        height: '50%',
        opacity: '0.5',
      },
      activating: {
        title: 'Activating',
        color: rfcColors.activating,
        height: '50%',
        opacity: '0.5',
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.#handleDocumentClick);
  }

  // if properties info, interest, or need don't equal 0, validate the role for comms
  firstUpdated() {
    if (this.info !== 0 || this.interest !== 0 || this.need !== 0) {
      this.#validateRoleForComms(false);
    }
  }

  #handleDocumentClick = event => {
    const popup = this.shadowRoot.querySelector('omni-tile');
    if (event.composedPath().includes(popup)) return;
    this.dispatchNewEvent('close-rfc-sliders');
  };

  #validateRoleForComms = async (updateText = true) => {
    const { info, interest, need } = this;
    let maxKey = null;
    let maxHeight = 0;

    this.barLabels = produce(this.barLabels, draft => {
      // These formulas where created by doing a regression on Studios rfc slider outputs for each bar's regression coefficients.
      draft.building.height = 60 + 0.4 * info - 0.1 * interest - 0.4 * need;
      draft.reinforcing.height = 10 + 0.4 * info + 0.1 * interest + 0.4 * need;
      draft.impacting.height = 100 - 0.4 * info - 0.4 * interest - 0.1 * need;
      draft.activating.height = 50 - 0.4 * info + 0.4 * interest + 0.1 * need;
      // Find the bar with the largest height and set opacities accordingly
      Object.keys(draft).forEach(key => {
        if (draft[key].height > maxHeight) {
          maxHeight = draft[key].height;
          maxKey = key;
        }
        draft[key].opacity = '0.5';
      });
      draft[maxKey].opacity = '1';
    });
    this.rfc = maxKey;

    if (updateText) {
      this.rfcTitle = this.rfcPlaceholders[maxKey].title;
      this.rfcDescription = this.rfcPlaceholders[maxKey].description;
    }
  };

  #onSliderChange = async (value, slider) => {
    // If all sliders are at 50 all the rfc bars will be at the same height so we programmatically move one of the sliders to 51 to break the tie.
    if (parseInt(value, 10) !== 50) return;
    if (this.info === 50 && this.interest === 50 && slider === 'need') {
      this.need = 51;
    } else if (this.info === 50 && this.need === 50 && slider === 'interest') {
      this.interest = 51;
    } else if (this.interest === 50 && this.need === 50 && slider === 'info') {
      this.info = 51;
    }
    await this.updateComplete;
    this.#validateRoleForComms();
  };

  #onSliderInput = (value, slider) => {
    this[slider] = parseInt(value, 10);
    this.#validateRoleForComms();
  };

  #saveRFC = () => {
    const {
      rfc,
      rfcTitle,
      rfcDescription,
      info,
      interest,
      need,
      percentageIndex,
      audienceIndex,
    } = this;

    const groupName =
      this.audience.stagePercentages[this.percentageIndex]?.group;

    this.dispatchNewEvent('save-rfc-sliders', {
      detail: {
        rfc,
        rfcTitle,
        rfcDescription,
        info,
        interest,
        need,
        percentageIndex,
        audienceIndex,
        groupName,
      },
    });
    this.#closeRFC();
  };

  #closeRFC = () => {
    this.dispatchNewEvent('close-rfc-sliders');
  };

  #cancelTileDrag = e => {
    e.stopPropagation();
  };

  get #slidersTemplate() {
    const sliders = Object.keys(this.sliderLabels);
    return html`
      <div class="sliders">
        ${map(
          sliders,
          slider => html`
            <div class="slider">
              <div class="slider-labels">
                ${map(
                  this.sliderLabels[slider],
                  label => html`
                    <div class="slider-label">
                      <p>${label.title}</p>
                      <omni-tooltip>
                        <omni-icon
                          slot="invoker"
                          class="is-size-2"
                          icon-id="omni:informative:info"
                        ></omni-icon>
                        <div slot="content">${label?.info}</div>
                      </omni-tooltip>
                    </div>
                  `,
                )}
              </div>
              <input
                @dragstart=${this.#cancelTileDrag}
                @pointerdown=${this.#cancelTileDrag}
                @pointermove=${this.#cancelTileDrag}
                @pointerup=${this.#cancelTileDrag}
                @pointerout=${this.#cancelTileDrag}
                type="range"
                id=${slider}
                name=${slider}
                min="0"
                max="100"
                step="1"
                value="0"
                .value=${this[slider]}
                @input=${e => this.#onSliderInput(e.target.value, slider)}
                @change=${e => this.#onSliderChange(e.target.value, slider)}
              />
            </div>
          `,
        )}
      </div>
    `;
  }

  get #barsTemplate() {
    const bars = Object.keys(this.barLabels);
    return html`
      <div class="bars">
        ${map(
          bars,
          bar => html`
            <div style="opacity: ${this.barLabels[bar].opacity}">
              <div
                class="bar-color"
                style=${styleMap({
                  background: this.barLabels[bar].color,
                  height: `${this.barLabels[bar].height}%`,
                })}
              ></div>
              <p class="bar-label">${this.barLabels[bar].title}</p>
            </div>
          `,
        )}
      </div>
    `;
  }

  get #contentTemplate() {
    return html`
      <div class="content-container">
        <div class="rfc-content">
          <p class="content-label">Role for Comms title</p>
          <input
            @dragstart=${this.#cancelTileDrag}
            @pointerdown=${this.#cancelTileDrag}
            @pointermove=${this.#cancelTileDrag}
            @pointerup=${this.#cancelTileDrag}
            @pointerout=${this.#cancelTileDrag}
            class="input"
            type="text"
            placeholder="Enter title"
            @change=${e => {
              this.rfcTitle = e.target.value;
            }}
            .value=${this.rfcTitle}
          />
        </div>
        <div class="rfc-content">
          <p class="content-label">Description</p>
          <omni-textarea
            @dragstart=${this.#cancelTileDrag}
            @pointerdown=${this.#cancelTileDrag}
            @pointermove=${this.#cancelTileDrag}
            @pointerup=${this.#cancelTileDrag}
            @pointerout=${this.#cancelTileDrag}
            rows="3"
            maxRows="10"
            resize
            placeholder="Enter description"
            @change=${e => {
              this.rfcDescription = e.target.value;
            }}
            .value=${this.rfcDescription}
          ></omni-textarea>
        </div>
        <div class="button-container">
          <button class="button is-text" @click=${this.#closeRFC}>
            Cancel
          </button>
          <button class="button is-primary" @click=${this.#saveRFC}>
            Save
          </button>
        </div>
      </div>
    `;
  }

  render() {
    const groupName =
      this.audience.stagePercentages[this.percentageIndex]?.group;
    return html`
      <omni-style>
        <omni-tile drag-able>
          <omni-toolbar slot="header">
            <h3 slot="start" class="title is-4 has-text-weight-normal">
              Role for Comms:
              ${groupName || `${this.audience.name} - ${this.stage.name}`}
            </h3>
            <button
              slot="end"
              class="button is-text is-shadowless"
              @click=${() => this.dispatchNewEvent('close-rfc-sliders')}
            >
              <omni-icon
                class="is-size-1"
                icon-id="omni:interactive:close"
              ></omni-icon>
            </button>
          </omni-toolbar>
          <div class="rfc-container">
            ${this.#slidersTemplate} ${this.#barsTemplate}
            ${this.#contentTemplate}
          </div>
        </omni-tile>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-audience-rfc-sliders', CampaignRFCSliders);
